import React, { createRef, useEffect, useState } from "react";
import styled from "styled-components"
import { EColor, EIconGlyph, ESize } from "../../styleConstants";
import { breakPoint, rem, styleReset } from "../styleUtils";
import { Validator } from "../validators";
import Icon from "./icon";

const FieldWrapper = styled.div`
    width: 100%;
    position: relative;
    box-sizing: border-box;
    color: ${EColor.FloralWhite};
    margin-bottom: ${rem(ESize.small)};
`

type LabelProps = {
    active: boolean
    focus: boolean
    hasTrailingIcon: boolean
    hasLeadingIcon: boolean
}
const Label = styled.label<LabelProps>`
    ${styleReset}

    position: absolute;
    top: ${rem(26+4)};
    left: ${props => props.hasLeadingIcon ? rem(24 + 1 + 36) : rem(24+1)};
    font-size: ${rem(22)}
    user-select: none;
    pointer-events: none;
    
    transform-origin: 0px 0px;
    transform: ${props => props.active ? `scale(0.72) translateY(-${rem(15)})` : `scale(1) translateY(0)`};
    color: ${(props) => {
        if (props.focus) {
            return EColor.SunglowYellow
        } else if (!props.focus && props.active) {
            return "#A29E99"
        } else {
            return EColor.FloralWhite
        }
    }};
    transition: color 0.2s, transform 0.2s;
`

type LabelFocusProps = {
    active: boolean
}
const LabelFocus = styled.span<LabelFocusProps>`
    opacity: ${props => props.active ? 1 : 0}
`

type LabelBlurProps = {
    active: boolean
}
const LabelBlur = styled.span<LabelBlurProps>`
    opacity: ${props => props.active ? 1 : 0}
    position: absolute;
    top: 0;
    left: 0;
`

type ErrorProps = {
    hasError: boolean
}
const Error = styled.div<ErrorProps>`
    font-size: ${rem(16)};
    color: ${EColor.TapestryRed};
    font-weight: 400;
    user-select: none;
    opacity: ${props => props.hasError ? 1 : 0};
    max-height: ${props => props.hasError ? rem(32) : rem(0)};
    padding-top: ${props => props.hasError ? rem(8) : rem(0)};
    overflow: hidden;
    
    transition: opacity 0.2s, max-height 0.2s, padding-top 0.2s;
`

type InputProps = {
    hasError: boolean
    hasTrailingIcon: boolean
    trailingButtonPadding: number
    hasLeadingIcon: boolean
    hasPlaceholder: boolean
}
const Input = styled.input<InputProps>`
    ${styleReset}
    
    background: #000000;
    width: 100%;

    height: ${rem(76)};
    padding-top: ${props => props.hasPlaceholder ? rem(38) : rem(18)};
    padding-left: ${props => props.hasLeadingIcon ? rem(24 + 36) : rem(24)};
    padding-right: ${props => props.hasTrailingIcon ? rem(24 + 36) : rem(24)};

    ${props => {
        if (props.trailingButtonPadding > 0) {
            return `padding-right: ${rem(24 + props.trailingButtonPadding * 12)};`
        }
    }}
    
    
    padding-bottom: ${rem(16)};

    border-radius: ${rem(8)};
    
    border: ${rem(1)} solid #A29E99;
    border-color: ${props => props.hasError ? EColor.TapestryRed : "#A29E99" }

    background: transparent;
    box-shadow: inset 0px 0px 0px ${rem(76)} ${EColor.ColombianBlue};
    box-sizing: border-box;

    font-size: ${rem(22)}
    transition: border-color 0.2s;
`

const InputIcon = styled.div`
    position: absolute;
    height: ${rem(76)};
    top: 0;
    display: flex;
    align-items: center;
    pointer-events: none;
`

const LeadingIcon = styled(InputIcon)`
    left: ${rem(ESize.default)};
`

const TrailingIcon = styled(InputIcon)`
    right: ${rem(ESize.default)};
    pointer-events: all;
`

const TrailingButtonContainer = styled(InputIcon)`
    right: ${rem(ESize.default)};
    cursor: pointer;
    pointer-events: all;
`

const TrailingButton = styled.div<{
    background: EColor
}>`
    font-size: ${rem(14)};
    font-weight: 700;
    color: ${EColor.Russian};
    background: ${props => props.background};
    border-radius: ${rem(8)};
    padding: ${rem(12)};
`

export default function TextField ({
    name,
    type = "text",
    id,
    leadingIcon,
    trailingIcon,
    assitiveText,
    defaultValue,
    placeholder,
    placeholderFocus,
    readOnly,
    trailingButton,
    trailingButtonBackground = EColor.SunglowYellow,
    onClickTrailingButton=()=>{},
    onChangeCallback=()=>{},
    onValidateCallback=()=>{},
    runValidation = 0,
    validators = [],
    validateOnBlur = false,
}: {
    name: string
    type?: "text"|"password"|"password-toggleable"
    id?: string
    leadingIcon?: EIconGlyph
    trailingIcon?: EIconGlyph
    assitiveText?: string|JSX.Element
    placeholder?: string|JSX.Element
    placeholderFocus?: string|JSX.Element
    defaultValue?: string
    readOnly?: boolean
    trailingButton?: string
    trailingButtonBackground?: EColor
    onChangeCallback?: (value: string, name: string) => void
    onValidateCallback?: (status: "valid"|"pending"|"invalid", name: string, value: string) => void
    onClickTrailingButton?: () => void
    runValidation?: number
    validators?: Validator[]
    validateOnBlur?: boolean
}) {
    const [value,           setValue]           = useState<string>(defaultValue ? defaultValue : "")
    const [inFocus,         setInFocus]         = useState<boolean>(false)
    const [isFilled,        setIsFilled]        = useState<boolean>(value.length > 0)
    const [hasError,        setHasError]        = useState<boolean>(value == "testerror" ? true : false)
    const [errorMessage,    setErrorMessage]    = useState<string|JSX.Element>("")
    const [inputType,       setInputType]       = useState<"password"|"text">(type === "password-toggleable" ? "password" : type)

    const hasPlaceholder = (placeholder || placeholderFocus) ? true : false
    
    const [lastValidatedValue, setLastValidatedValue] = useState<string>("--------------")
    const input = createRef<HTMLInputElement>();

    if (type == "password-toggleable") {
        if (inputType == "text") 
            trailingIcon = EIconGlyph.eye_on
        
        if (inputType == "password") 
            trailingIcon = EIconGlyph.eye_off
    }

    const runValidators = (forced: boolean = false) => {
        if (value !== lastValidatedValue && (runValidation > 0 || forced)) {
            setLastValidatedValue(value)

            async function validateInput() {
                onValidateCallback("pending", name, value)
                for (const validator of validators) {
                    let result = await validator(value)
                    if (!result.valid) {
                        setHasError(true)
                        setErrorMessage(result.message)
                        onValidateCallback("invalid", name, value)
                        return
                    }
                }
                setHasError(false)
                setTimeout(() => {
                    onValidateCallback("valid", name, value)
                }, 10)
            }

            validateInput()
        }
    }

    useEffect(() => {
        runValidators()
    }, [runValidation])

    return (
        <FieldWrapper>
            {hasPlaceholder && 
                <Label 
                    active={inFocus || isFilled} 
                    focus={inFocus}
                    hasTrailingIcon={trailingIcon ? true : false}
                    hasLeadingIcon={leadingIcon ? true : false}
                >
                    {/* <LabelFocus active={inFocus || isFilled}>
                        {placeholderFocus || placeholder}
                    </LabelFocus> */}
                    <LabelBlur active={!inFocus && !isFilled}>
                        {placeholder}
                    </LabelBlur>
                </Label>
            }

            {leadingIcon && 
                <LeadingIcon>
                    <Icon icon={leadingIcon} size={ESize.default} color={inFocus ? EColor.SunglowYellow : EColor.FloralWhite}></Icon>
                </LeadingIcon>
            }

            {trailingButton && !trailingIcon && 
                <TrailingButtonContainer onClick={() => { onClickTrailingButton() }}>
                    <TrailingButton background={trailingButtonBackground}>
                        {trailingButton}
                    </TrailingButton>
                </TrailingButtonContainer>
            }

            {trailingIcon && !trailingButton && 
                <TrailingIcon 
                    style={{cursor: type == "password-toggleable" ? "pointer" : "default"}} 
                    onPointerDown={(event) => {
                        //Toggles visible/hidden password
                        event.preventDefault()
                        if (type == "password-toggleable") {
                            if (inputType == "text") 
                                setInputType("password")

                            if (inputType == "password") 
                                setInputType("text")
                        }
                    }
                }>
                    <Icon icon={trailingIcon} size={ESize.default} color={inFocus ? EColor.SunglowYellow : EColor.FloralWhite}></Icon>
                </TrailingIcon>
            }


            <Input 
                ref={input}
                name={name}
                id={id}
                type={inputType} 
                value={value}
                hasError={hasError}
                hasPlaceholder={hasPlaceholder}
                readOnly={readOnly}
                
                hasTrailingIcon={trailingIcon ? true : false}
                trailingButtonPadding={trailingButton ? trailingButton.length : 0}
                hasLeadingIcon={leadingIcon ? true : false}

                onFocus={() => {
                    setInFocus(true)
                }}
                onBlur={() => {
                    setInFocus(false)
                    
                    if (validateOnBlur) {
                        if (value.length > 0) {
                            runValidators(true)
                        } else {
                            setLastValidatedValue("--------------")
                            setHasError(false)
                        }
                    }
                }}
                onChange={(event) => {
                    
                    setValue(event.target.value)
                    setIsFilled(event.target.value.length > 0 ? true : false)
                    onChangeCallback(event.target.value, name)
                    
                }}
            />
            <Error hasError={hasError}>
                {errorMessage}
            </Error>
        </FieldWrapper>
    )
}